import { useState } from "react";
import { Reply, SmilePlus, Pencil, Trash2, Forward, Check, CheckCheck, Pin, MoreHorizontal } from "lucide-react";
import type { Message, ReactionTally } from "@glass/shared";
import { Avatar } from "./Avatar";

const REACTIONS = ["❤️", "👍", "🔥", "😂", "😮", "😢", "🙏"];

interface MessageBubbleProps {
  msg: Message;
  isMe: boolean;
  showAvatar: boolean;
  authorName: string;
  authorAvatar?: string | null;
  onReply: (m: Message) => void;
  onReact: (m: Message, emoji: string) => void;
  onEdit: (m: Message) => void;
  onDelete: (m: Message, forEveryone: boolean) => void;
  onForward: (m: Message) => void;
  onPin: (m: Message) => void;
  onJumpTo: (id: string) => void;
}

export function MessageBubble({ msg, isMe, showAvatar, authorName, authorAvatar, onReply, onReact, onEdit, onDelete, onForward, onPin, onJumpTo }: MessageBubbleProps) {
  const [menu, setMenu] = useState(false);
  const [reactPicker, setReactPicker] = useState(false);

  if (msg.kind === "system") {
    return (
      <div className="self-center my-2 pill text-ink-300">
        {msg.body}
      </div>
    );
  }

  const time = new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div className={`group flex items-end gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
      {!isMe && <div className="w-8 shrink-0">{showAvatar && <Avatar name={authorName} url={authorAvatar} size={28} />}</div>}
      <div className={`relative max-w-[75%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
        {!isMe && showAvatar && (
          <div className="text-xs text-accent-200 px-2 mb-0.5">{authorName}</div>
        )}
        {msg.reply_to && (
          <button className="text-xs text-ink-300 mb-1 px-3 py-1 rounded-xl bg-white/5 border-l-2 border-accent-400 truncate max-w-full"
            onClick={() => onJumpTo(msg.reply_to!.id)}>
            <span className="text-accent-200 mr-1">↩</span>
            {msg.reply_to.preview}
          </button>
        )}
        <div className={`bubble ${isMe ? "bubble-me" : "bubble-them"}`}>
          {msg.kind === "text" && <div className="whitespace-pre-wrap">{msg.body}</div>}
          {msg.kind === "photo" && msg.media && (
            <div className="space-y-1.5">
              <img src={msg.media.url} className="rounded-xl max-h-80" alt="" />
              {msg.body && <div className="whitespace-pre-wrap">{msg.body}</div>}
            </div>
          )}
          {msg.kind === "video" && msg.media && (
            <div className="space-y-1.5">
              <video src={msg.media.url} className="rounded-xl max-h-80" controls />
              {msg.body && <div className="whitespace-pre-wrap">{msg.body}</div>}
            </div>
          )}
          {msg.kind === "voice" && msg.media && (
            <audio src={msg.media.url} controls />
          )}
          {msg.kind === "document" && msg.media && (
            <a href={msg.media.url} download className="flex items-center gap-2 underline">
              📎 {msg.media.name || "Файл"} <span className="text-xs opacity-70">({Math.round((msg.media.size || 0) / 1024)} KB)</span>
            </a>
          )}
          {msg.kind === "geo" && msg.geo && (
            <a className="underline" href={`https://www.openstreetmap.org/?mlat=${msg.geo.lat}&mlon=${msg.geo.lon}#map=15/${msg.geo.lat}/${msg.geo.lon}`} target="_blank" rel="noreferrer">📍 Геопозиция {msg.geo.live_until ? "(живая)" : ""}</a>
          )}
          {msg.kind === "poll" && msg.poll && (
            <div className="space-y-1.5">
              <div className="font-semibold">{msg.poll.question}</div>
              {msg.poll.options.map((o, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-white/10 rounded-lg px-2 py-1">
                  <span>{o}</span>
                  <span className="text-xs opacity-70">{msg.poll!.votes?.[i] || 0}</span>
                </div>
              ))}
            </div>
          )}
          <div className={`flex items-center gap-1.5 mt-1 text-[11px] ${isMe ? "text-white/70 justify-end" : "text-ink-400"}`}>
            {msg.edited_at && <span>изменено</span>}
            <span>{time}</span>
            {isMe && (
              msg.read_by && msg.read_by.length > 0
                ? <CheckCheck size={13} className="checkmark-read" />
                : <Check size={13} className="checkmark" />
            )}
          </div>
        </div>
        {Array.isArray(msg.reactions) && msg.reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isMe ? "justify-end" : "justify-start"}`}>
            {msg.reactions.map((r: ReactionTally) => (
              <button key={r.emoji} className="pill text-xs" onClick={() => onReact(msg, r.emoji)}>
                {r.emoji} {r.count}
              </button>
            ))}
          </div>
        )}

        <div className={`absolute top-0 ${isMe ? "-left-9" : "-right-9"} opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 z-10`}>
          <button className="btn btn-ghost p-1.5 rounded-full" title="Ответить" onClick={() => onReply(msg)}><Reply size={14} /></button>
          <button className="btn btn-ghost p-1.5 rounded-full" title="Реакция" onClick={() => setReactPicker((v) => !v)}><SmilePlus size={14} /></button>
          <button className="btn btn-ghost p-1.5 rounded-full" title="Ещё" onClick={() => setMenu((v) => !v)}><MoreHorizontal size={14} /></button>
        </div>
        {reactPicker && (
          <div className="absolute z-20 -top-9 glass-strong p-1 rounded-full flex gap-1">
            {REACTIONS.map((e) => (
              <button key={e} className="hover:bg-white/10 rounded-full px-1.5 py-0.5 text-lg" onClick={() => { onReact(msg, e); setReactPicker(false); }}>{e}</button>
            ))}
          </div>
        )}
        {menu && (
          <div className={`absolute z-20 ${isMe ? "right-0" : "left-0"} top-9 glass-strong p-1 rounded-xl text-sm min-w-[180px]`}>
            <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10" onClick={() => { onForward(msg); setMenu(false); }}><Forward size={14} /> Переслать</button>
            <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10" onClick={() => { onPin(msg); setMenu(false); }}><Pin size={14} /> Закрепить</button>
            {isMe && msg.kind === "text" && (
              <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10" onClick={() => { onEdit(msg); setMenu(false); }}><Pencil size={14} /> Изменить</button>
            )}
            <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-red-500/15 text-red-300" onClick={() => { onDelete(msg, false); setMenu(false); }}><Trash2 size={14} /> Удалить у себя</button>
            {isMe && (
              <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-red-500/15 text-red-300" onClick={() => { onDelete(msg, true); setMenu(false); }}><Trash2 size={14} /> Удалить у всех</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
